import { getWsInstance } from "../../api/WsManagement";
import { setAlternateSignalingUrl } from "../../api/robocore/wrtc_connection";
import { SignalingConnection } from "../../api/signaling/SignalingConnection";
import { Robot } from "../../models/Robot";

export interface VideoRTCFeed {
  roomId: string;
  peerConnection: RTCPeerConnection;
  mediaStream: MediaStream;
  signalingConnection: SignalingConnection;
}

const connections = new Map<string, VideoRTCFeed>();

export async function createPeerVideoConnection(robot: Robot) {
  const ws = getWsInstance();
  const userId = ws.uiService.user._id;
  const signalingConnection = new SignalingConnection({
    group: `video-${userId}`,
    deviceId: robot._id,
    token: robot.token,
  });
  let baseUrl = '';
  if (window.location.href.includes('?signaling=')) {
    const signalingParam = new URLSearchParams(window.location.search).get('signaling');
    baseUrl = `ws://${signalingParam}:28801`;
    setAlternateSignalingUrl(baseUrl);
  }
  signalingConnection.config.baseUrl = baseUrl;
  await signalingConnection.connect();
  const device = await signalingConnection.getDevice() as any;
  console.log('VIDEO WRTC DEVICE: ', device);
  const roomId = device._id;
  const turnServer = `stun:${device.creds.turnServer},turn:${device.creds.turnServer}`;
  const turnCred = device.creds.turnCred;
  if (connections.has(roomId)) {
    const feed = connections.get(roomId)!;
    feed.peerConnection.close();
    feed.signalingConnection.close();
  }
  //   const iceServers = await loadRecord<StunTurnCredentials>(`credentials/StunTurn`);
  // console.log(iceServers);
  const configuration = {
    iceServers: [],
    iceCandidatePoolSize: 10
  };
  if (device.creds.useTurn !== 'direct') {
    const iceServers = turnServer.split(',').map((server) => server.trim());
    const [username, credential] = turnCred.split(':');
    for (const server of iceServers) {
      if (server.startsWith('stun:')) {
        (configuration.iceServers as any).push({
          urls: server
        });
      } else if (server.startsWith('turn:')) {
        (configuration.iceServers as any).push({
          urls: server,
          username,
          credential
        });
      }
    }
  }
  const feed: VideoRTCFeed = {
    roomId,
    peerConnection: new RTCPeerConnection(configuration),
    mediaStream: new MediaStream(),
    signalingConnection,
  };
  registerPeerConnectionListeners(feed);
  connections.set(roomId, feed);
  return feed;
}

export function getPeerConnection(roomId: string) {
  return connections.get(roomId);
}

export function deletePeerConnection(roomId: string) {
  if (!connections.has(roomId)) {
    return;
  }
  const feed = connections.get(roomId)!;
  feed.peerConnection.close();
  feed.signalingConnection.close();
  connections.delete(roomId);
}

export function registerPeerConnectionListeners(feed: VideoRTCFeed) {
  console.log('VIDEO WEBRTC REGISTERING LISTENERS');
  const { peerConnection, mediaStream, signalingConnection } = feed;
  let retryTimer: any = null;
  const setupRemote = async (offer: any) => {
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
    signalingConnection.sendDelete({ offer: true });
    await peerConnection.setRemoteDescription(new RTCSessionDescription(offer));
    const answer = await peerConnection.createAnswer();
    await peerConnection.setLocalDescription(answer);
    const sdpCandidateArray = offer.sdp.split('\n').filter((line: string) => line.startsWith('a=candidate'));
    for (const candidate of sdpCandidateArray) {
      const candidateSDP = candidate.substring(2);
      await peerConnection.addIceCandidate({ candidate: candidateSDP, sdpMid: '0', sdpMLineIndex: 0 });
    }
    console.log('VIDEO WEBRTC ANSWER');
    signalingConnection.sendReplace({ answer });
  }
  signalingConnection.config.onReplace = async (replace: any) => {
    if (replace.offer) {
      console.log('VIDEO WEBRTC OFFER REPLACE');
      await setupRemote(replace.offer);
    }
  };
  signalingConnection.config.onUpdate = async (update: any) => {
    if (update.offer) {
      console.log('VIDEO WEBRTC OFFER UPDATE in state:', feed.peerConnection.connectionState);
      // if (feed.peerConnection.connectionState === 'connected' || 
      //     feed.peerConnection.connectionState === 'connecting') {
      //   console.log('VIDEO WEBRTC OFFER UPDATE: already connected');
      //   return;
      // }
      await setupRemote(update.offer);
    }
  };
  peerConnection.addEventListener('track', event => {
    // console.log('Got remote track:', event.streams[0]);
    event.streams[0].getTracks().forEach(track => {
      // console.log('Add a track to the remoteStream:', track);
      mediaStream.addTrack(track);
    });
  });
  peerConnection.addEventListener('icecandidate', async event => {
    if (!event.candidate) {
      // console.log('Got final candidate!');
      const dataGram = { calleeCandidatesSent: true };
      signalingConnection.sendReplace(dataGram);
      return;
    }
    const candidate = event.candidate.toJSON();
    // console.log('Got candidate: ', event.candidate);
    const dataGram = { calleeCandidates: [candidate] };
    signalingConnection.sendAppend(dataGram);
  });
  peerConnection.addEventListener('icegatheringstatechange', () => {
    // console.log(`ICE gathering state changed: ${peerConnection.iceGatheringState}`);
  });
  peerConnection.addEventListener('connectionstatechange', () => {
    console.log(`VIDEO WEBRTC CONNECTION STATE CHANGE: ${peerConnection.connectionState}`);
    if (peerConnection.connectionState === 'failed' || peerConnection.connectionState === 'disconnected') {
      deletePeerConnection(feed.roomId);
    }
  });
  peerConnection.addEventListener('onicecandidateerror', (event: any) => {
    // console.error('ICE Candidate Error:', event.errorCode, event.errorText);
  });
  peerConnection.addEventListener('signalingstatechange', () => {
    // console.log(`Signaling state change: ${peerConnection.signalingState}`);
  });
  peerConnection.addEventListener('iceconnectionstatechange', () => {
    // console.log(`ICE connection state change: ${peerConnection.iceConnectionState}`);
  });
  retryTimer = setInterval(() => {
    signalingConnection.sendReplace({ clientReady: true });
  }, 5000);
  signalingConnection.sendReplace({ clientReady: true });
  
}
